import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".campaigns"

// Mounts on two disjoint views: the campaigns list (subdomain/campaigns/index.html.haml, paired
// with the shared `datatable` controller on the same `.content-box`) and the create-campaign form
// (subdomain/campaigns/new.html.haml). Most targets below are optional and `hasXTarget`-guarded so
// each view simply no-ops on the other's elements.
export default class extends Controller {
  static get targets() {
    return [
      "table", "dateRangeInput", "search", "creatorFilter", "teamFilter",
      "nameInput", "categorySelect", "submitButton", "deleteButton",
    ]
  }

  static get values() {
    return { begin: String, end: String }
  }

  connect() {
    if (this.hasDateRangeInputTarget) { this.initDateRangePicker() }
    if (this.hasCreatorFilterTarget) { this.initFilterSelect(this.creatorFilterTarget, "Created By") }
    if (this.hasTeamFilterTarget) { this.initFilterSelect(this.teamFilterTarget, "Team") }

    // select2 announces a pick with jQuery's `.trigger("change")`, not a native dispatch, so this
    // has to stay on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-moveable-filter", () => this.reload())

    if (this.hasCategorySelectTarget) { this.initCategorySelect() }

    if (this.hasNameInputTarget) { this.validateCreate() }
  }

  disconnect() {
    $(this.element).off(JQUERY_NAMESPACE)

    if (this.hasDateRangeInputTarget) {
      const picker = $(this.dateRangeInputTarget).data("daterangepicker")
      if (picker) { picker.remove(); }
      $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE)
    }

    [this.hasCreatorFilterTarget && this.creatorFilterTarget, this.hasTeamFilterTarget && this.teamFilterTarget]
      .filter(Boolean).forEach((el) => {
        $(el).off(JQUERY_NAMESPACE)
        if ($(el).data("select2")) { $(el).select2("destroy") }
      })

    if (this.hasCategorySelectTarget) {
      $(this.categorySelectTarget).off(JQUERY_NAMESPACE)
      if ($(this.categorySelectTarget).data("select2")) { $(this.categorySelectTarget).select2("destroy") }
    }
  }

  // Calls the jQuery plugin directly with this page's own options, rather than going through
  // window.ltDateRangePicker as the dashboard does.
  initDateRangePicker() {
    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end))

    // The bindings themselves stay jQuery -- daterangepicker announces show/hide through jQuery's
    // event bus, not a native dispatch -- but the handler bodies are plain DOM work on this
    // page's own markup and don't need jQuery.
    $(this.dateRangeInputTarget).
      on(`show.daterangepicker${JQUERY_NAMESPACE}`, () => { document.querySelector(".date-range-picker").classList.add("is-open") }).
      on(`hide.daterangepicker${JQUERY_NAMESPACE}`, () => { document.querySelector(".date-range-picker").classList.remove("is-open") })
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`
    this.reload({ range: newRange })
  }

  // A second, undebounced binding on the same input the shared `datatable` controller also binds
  // with its own 500ms debounce. Both stay: dropping this one makes filtering visibly slower.
  onSearchKeyup() {
    $(this.tableTarget).DataTable().search(this.searchTarget.value).draw()
  }

  initFilterSelect(element, placeholder) {
    $(element).select2({ placeholder, width: "100%" })
    $(element).on(`select2:select${JQUERY_NAMESPACE}`, (event) => this.markSelect2Active(event))
    $(element).on(`select2:unselect${JQUERY_NAMESPACE}`, (event) => this.markSelect2Inactive(event))
  }

  // select2-container--filled is a shared utility class (also used by custom_packaging_bundles and
  // automations) styled outside this controller, and it lands on select2's own wrapper element
  // rather than this controller's mount, so it stays a plain classList string, not a Classes API
  // entry.
  markSelect2Active(event) {
    event.currentTarget.parentElement.querySelector(".select2")?.classList.add("select2-container--filled")
  }

  markSelect2Inactive(event) {
    event.currentTarget.parentElement.querySelector(".select2")?.classList.remove("select2-container--filled")
  }

  initCategorySelect() {
    $(this.categorySelectTarget).select2({ placeholder: "Choose a category", width: "calc(100% + 6px)" })
    $(this.categorySelectTarget).on(`select2:select${JQUERY_NAMESPACE}`, (event) => this.markSelect2Active(event))
    $(this.categorySelectTarget).on(`select2:unselect${JQUERY_NAMESPACE}`, (event) => this.markSelect2Inactive(event))

    if (this.categorySelectTarget.value) {
      this.markSelect2Active({ currentTarget: this.categorySelectTarget })
    }

    $(this.categorySelectTarget).on(`change${JQUERY_NAMESPACE}`, () => this.validateCreate())
  }

  validateCreate() {
    const valid = this.nameInputTarget.value.length > 0 && this.categorySelectTarget.value.length > 0
    this.submitButtonTarget.disabled = !valid
    return valid
  }

  // Delegated from the mount: the modal and every datatable row live inside `.content-box`, so
  // this still reaches rows the datatable replaces on each draw.
  onDeleteClick(event) {
    const link = event.target.closest(".delete-campaign-link")
    if (!link) { return }

    event.preventDefault()
    $("#campaign-delete-modal").modal("show")
    this.deleteButtonTarget.setAttribute("href", link.getAttribute("href"))
  }

  reload(extraParams = {}) {
    ProgressBar.start()

    // Scoped to this.element, matching the original jQuery traversal's own scoping, rather than a
    // document-wide query -- kept generic by class instead of enumerating the declared filter
    // targets, so an unlisted filter select added to the markup later is still picked up.
    const filterSelects = this.element.querySelectorAll(".datatable-moveable-filter, .datatable-filter")
    if (filterSelects.length > 0) {
      const filter = {}
      filterSelects.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")
        filter[key] = ($(el).select2("data") || []).map((option) => option.id)
      })
      extraParams["filter"] = filter
    }

    this.element.querySelectorAll(".datatable-moveable-text-filter, .datatable-text-filter").forEach((el) => {
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value
    })

    const params = Object.assign(this.currentParams(), extraParams)

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`
    this.tableTarget.setAttribute("data-source", replacement)
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop())
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"))
  }

  // Converts a bracket-notation query string (filter[creator][]=42) into the nested object
  // ({ filter: { creator: ["42"] } }) that `$.param()` re-serializes back into the same form.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1)
    const regex = /([^&=]+)=?([^&]*)/g
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "))

    const params = {}
    let match = regex.exec(query)
    while (match) {
      const k = decode(match[1])
      const v = decode(match[2])

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2)
        ;(params[key] || (params[key] = [])).push(v)
      } else {
        params[k] = v
      }

      match = regex.exec(query)
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1
      let target = obj
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i]
        if (!(key in target)) { target[key] = {} }
        target = target[key]
      }
      target[keyPath[lastKeyIndex]] = value
    }

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[")
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""))
        assignPath(params, levels, params[prop])
        delete params[prop]
      }
    })

    return params
  }
}
